/**
 * CampaignTracker - Campaign progress view
 * Shows: Campaign dropdown, stats, map with stores, click store to see postings/evidence
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Column, ColumnDef } from "@tanstack/react-table";
import { Link, useParams, useLocation } from "wouter";
import { TrackerLayout } from "./TrackerLayout";
import { TaskSubmissionNotes } from "./TaskSubmissionNotes";
import { getJson } from "@/lib/http";
import { copyTaskDefinitionIdsToParams, trackerApiQueryString } from "@/lib/trackerTaskDefinitionFilter";
import { useTrackerExportContext } from "@/hooks/useTrackerExportContext";
import {
    TrackerProgressExportButton,
    buildTrackerExportFilenameStem,
} from "@/components/tracker/TrackerProgressExportButton";
import { buildTrackerCampaignProgressCsv } from "@/lib/trackerProgressExport";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { DataTable } from "@/components/tables/data-table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select";
import {
    MapContainer,
    TileLayer,
    CircleMarker,
    Popup,
    useMap
} from "react-leaflet";
import L from "leaflet";
import type {
    TrackerCampaignResponse,
    TrackerCampaignMarker,
    TrackerTask,
    TrackerRouteStatus,
    TrackerCampaignListItem
} from "@shared/tracker-types";
import { Camera, X, Package, Trash2, ArrowUpDown } from "lucide-react";
import { FormatStatusTable } from "@/components/shared/FormatStatusTable";
import { ApprovedInstallerImagesTab } from "@/pages/campaign-details/ApprovedInstallerImagesTab";
import { ReportReadyImagesTab } from "@/pages/campaign-details/ReportReadyImagesTab";
import { TrackerMapLegend } from "./TrackerMapLegend";

const DEFAULT_MAP_CENTER: [number, number] = [53.35, -6.26];
const POLL_INTERVAL_ACTIVE = 15000;
const POLL_INTERVAL_BACKGROUND = 60000;
const CAMPAIGN_TABLE_COLUMNS_STORAGE_KEY = "cockpit_campaign_table_columns_v1";

function SortableHeader({
    label,
    column,
    align = "left",
}: {
    label: string;
    column: Column<TrackerCampaignListItem, unknown>;
    align?: "left" | "right";
}) {
    const button = (
        <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
            {label}
            <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
    );

    if (align === "right") {
        return <div className="text-right">{button}</div>;
    }

    return button;
}

interface CampaignListItem {
    id: number;
    name: string;
    status: string;
    retailerName?: string;
}

interface CampaignProgressResponse {
    cycleId: number;
    updatedAt: string;
    campaigns: TrackerCampaignListItem[];
}

function getStatusColor(status: TrackerRouteStatus): string {
    switch (status) {
        case "DONE": return "#22c55e";      // Green - Complete
        case "PARTIAL": return "#f59e0b";   // Amber - Partial delivery
        case "ACTIVE": return "#3b82f6";    // Blue - In Progress
        case "PENDING": return "#ef4444";   // Red - To Do
        case "ISSUE": return "#f97316";     // Orange - Issue
        case "ORPHAN": return "#a855f7";    // Purple - Orphan
        default: return "#6b7280";
    }
}

function getTaskStatusBadge(status: TrackerTask['status']) {
    switch (status) {
        case 'complete':
            return <Badge variant="success" className="text-xs">Complete</Badge>;
        case 'partial':
            return <Badge variant="warning" className="text-xs">Partial</Badge>;
        case 'issue':
            return <Badge variant="destructive" className="text-xs">Issue</Badge>;
        case 'pending':
        default:
            return <Badge variant="outline" className="text-xs">Pending</Badge>;
    }
}

function TaskCard({ task, onPhotoClick }: { task: TrackerTask; onPhotoClick: (url: string) => void }) {
    const actionWord = task.taskType === 'REMOVAL' ? 'Removed' : 'Delivered';
    const hasFormatBreakdown = task.mediaFormats && task.mediaFormats.length > 0;
    
    return (
        <div className="border rounded-lg p-3 space-y-2">
            {/* Header: Campaign name + Assignment ID + Status */}
            <div className="flex items-center justify-between">
                <div className="font-medium text-sm flex items-center gap-2">
                    {task.taskType === 'REMOVAL' ? (
                        <Trash2 className="h-3.5 w-3.5 text-orange-500" />
                    ) : (
                        <Package className="h-3.5 w-3.5 text-blue-500" />
                    )}
                    {task.campaignName}
                    {task.assignmentId && (
                        <span className="text-xs text-muted-foreground font-normal">
                            #{task.assignmentId}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    {task.taskType === 'REMOVAL' && (
                        task.basisUsed === 'DELIVERED'
                            ? <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-200">Confirmed delivery</Badge>
                            : <Badge variant="outline" className="text-xs text-muted-foreground">Planned</Badge>
                    )}
                    {getTaskStatusBadge(task.status)}
                </div>
            </div>
            
            {/* Pack name if present */}
            {task.packName && (
                <div className="text-xs text-muted-foreground">
                    Pack: {task.packName}
                </div>
            )}
            
            {/* Format-level breakdown - using shared component */}
            {hasFormatBreakdown ? (
                <FormatStatusTable 
                    formats={task.mediaFormats} 
                    deliveredLabel={actionWord}
                    compact
                />
            ) : (
                <FormatStatusTable 
                    formats={[{
                        formatId: task.taskId,
                        formatName: task.packName || task.campaignName,
                        planned: task.expectedQuantity,
                        delivered: task.completedQuantity
                    }]}
                    deliveredLabel={actionWord}
                    showTotal={false}
                    compact
                />
            )}
            
            {/* Photo count */}
            {task.photoCount > 0 && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Camera className="h-3 w-3" />
                    {task.photoCount} photo{task.photoCount !== 1 ? "s" : ""}
                </div>
            )}
            
            <TaskSubmissionNotes installerNote={task.installerNote} notes={task.notes} />
            
            {task.photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {task.photos.map(photo => (
                        <button
                            key={photo.photoId}
                            type="button"
                            onClick={() => onPhotoClick(photo.fullUrl)}
                            className="w-16 h-16 rounded border overflow-hidden hover:ring-2 ring-primary transition-all"
                        >
                            <img
                                src={photo.thumbUrl}
                                alt="Evidence"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = photo.fullUrl;
                                }}
                            />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function FitBounds({ markers }: { markers: TrackerCampaignMarker[] }) {
    const map = useMap();
    useEffect(() => {
        if (!markers.length) return;
        const points = markers.map(m => [m.lat, m.lng] as [number, number]);
        const bounds = L.latLngBounds(points);
        map.fitBounds(bounds, { padding: [40, 40] });
    }, [map, markers]);
    return null;
}

export default function CampaignTracker() {
    const params = useParams<{ campaignId?: string }>();
    const [, setLocation] = useLocation();
    const searchParams = new URLSearchParams(window.location.search);
    const retailerId = searchParams.get("retailerId");
    const cycleId = searchParams.get("cycleId");
    const taskDefinitionIds = searchParams.get("taskDefinitionIds");
    const campaignIdFromPath = params.campaignId ? parseInt(params.campaignId) : null;

    const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(campaignIdFromPath);
    const [selectedMarker, setSelectedMarker] = useState<TrackerCampaignMarker | null>(null);
    const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);
    const [favouritesCampaign, setFavouritesCampaign] = useState<TrackerCampaignListItem | null>(null);
    const [reportReadyCampaign, setReportReadyCampaign] = useState<TrackerCampaignListItem | null>(null);
    const campaignFocusRef = useRef<HTMLDivElement | null>(null);
    const shouldScrollToCampaignRef = useRef(false);

    useEffect(() => {
        if (campaignIdFromPath) setSelectedCampaignId(campaignIdFromPath);
    }, [campaignIdFromPath]);

    const [isTabVisible, setIsTabVisible] = useState(!document.hidden);
    useEffect(() => {
        const handleVisibility = () => setIsTabVisible(!document.hidden);
        document.addEventListener("visibilitychange", handleVisibility);
        return () => document.removeEventListener("visibilitychange", handleVisibility);
    }, []);

    const pollInterval = isTabVisible ? POLL_INTERVAL_ACTIVE : POLL_INTERVAL_BACKGROUND;
    
    // Build query string preserving both retailerId and cycleId
    const cycleQueryObj = new URLSearchParams();
    if (retailerId) cycleQueryObj.set("retailerId", retailerId);
    if (cycleId) cycleQueryObj.set("cycleId", cycleId);
    copyTaskDefinitionIdsToParams(cycleQueryObj, window.location.search);
    const cycleQuery = cycleQueryObj.toString() ? `?${cycleQueryObj.toString()}` : "";

    // Fetch campaigns for dropdown
    const { data: campaignsData } = useQuery<{ data: CampaignListItem[] } | CampaignListItem[]>({
        queryKey: ["tracker-campaigns-list", cycleId],
        queryFn: async () => {
            const p = new URLSearchParams();
            if (cycleId) p.set("cycleId", cycleId);
            p.set("page", "1");
            p.set("pageSize", "100");
            return await getJson(`/api/campaigns?${p.toString()}`);
        },
        enabled: !!cycleId,
        staleTime: 60000
    });

    const campaigns = useMemo(() => {
        if (!campaignsData) return [];
        return Array.isArray(campaignsData) ? campaignsData : campaignsData.data || [];
    }, [campaignsData]);

    const { data: campaignProgressData, isLoading: progressLoading } = useQuery<CampaignProgressResponse>({
        queryKey: ["tracker-campaign-progress", cycleId, taskDefinitionIds],
        queryFn: async () => {
            return await getJson<CampaignProgressResponse>(
                `/api/analytics/tracker/campaigns/${cycleId}${trackerApiQueryString(taskDefinitionIds)}`,
            );
        },
        enabled: !!cycleId,
        staleTime: 60000
    });

    const selectedCampaignSummary = useMemo(() => {
        if (!campaignProgressData?.campaigns?.length || !selectedCampaignId) return null;
        return campaignProgressData.campaigns.find(
            (campaign) => campaign.campaignId === selectedCampaignId
        ) || null;
    }, [campaignProgressData, selectedCampaignId]);

    const exportContext = useTrackerExportContext({
        reportLabel: "Progress by Campaign",
        retailerId,
        cycleId,
        taskDefinitionIds,
    });
    const campaignExportCsv = useMemo(() => {
        const campaigns = campaignProgressData?.campaigns ?? [];
        if (!exportContext || campaigns.length === 0) {
            return null;
        }
        return buildTrackerCampaignProgressCsv(campaigns, exportContext);
    }, [campaignProgressData?.campaigns, exportContext]);

    const handleCampaignChange = useCallback((value: string, options?: { scrollToDetails?: boolean }) => {
        const id = parseInt(value);
        shouldScrollToCampaignRef.current = Boolean(options?.scrollToDetails);
        setSelectedCampaignId(id);
        setSelectedMarker(null);
        if (cycleId) {
            setLocation(`/admin/activation-cockpit/campaign/${id}${cycleQuery}`);
        }
    }, [cycleId, cycleQuery, setLocation]);

    const campaignProgressColumns = useMemo<ColumnDef<TrackerCampaignListItem>[]>(() => [
        {
            accessorKey: "campaignName",
            meta: { title: "Campaign" },
            enableHiding: false,
            header: ({ column }) => <SortableHeader label="Campaign" column={column} />,
            cell: ({ row }) => (
                <button
                    type="button"
                    className="text-left text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:underline"
                    onClick={(event) => {
                        event.stopPropagation();
                        handleCampaignChange(String(row.original.campaignId), { scrollToDetails: true });
                    }}
                >
                    {row.original.campaignName}
                </button>
            )
        },
        {
            accessorKey: "status",
            meta: { title: "Status" },
            enableHiding: false,
            header: ({ column }) => <SortableHeader label="Status" column={column} />,
            cell: ({ row }) => (
                <Badge variant="outline" className="capitalize">
                    {row.original.status || "unknown"}
                </Badge>
            )
        },
        {
            id: "storeProgress",
            accessorFn: (row) => row.completionPercent,
            meta: { title: "Store Progress" },
            enableHiding: false,
            header: ({ column }) => <SortableHeader label="Store Progress" column={column} />,
            cell: ({ row }) => {
                return (
                    <div className="w-[220px] space-y-1">
                        <Progress value={row.original.completionPercent} className="h-2" />
                        <div className="text-xs text-muted-foreground">
                            {row.original.totalStores > 0
                                ? `${row.original.storeDoneCount}/${row.original.totalStores} done (${row.original.completionPercent}%)`
                                : "No stores"}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            Started: {row.original.startedPercent}%
                        </div>
                    </div>
                );
            }
        },
        {
            accessorKey: "retailerReadyImagesCount",
            meta: { title: "Retailer Ready Images" },
            header: ({ column }) => <SortableHeader label="Retailer Ready Images" column={column} align="right" />,
            cell: ({ row }) => (
                <div className="text-right">
                    <button
                        type="button"
                        className="text-primary underline underline-offset-2 hover:text-primary/80"
                        onClick={(event) => {
                            event.stopPropagation();
                            setFavouritesCampaign(row.original);
                        }}
                    >
                        {row.original.retailerReadyImagesCount}
                    </button>
                </div>
            )
        },
        {
            accessorKey: "reportReadyImagesCount",
            meta: { title: "Report Ready Images" },
            header: ({ column }) => <SortableHeader label="Report Ready Images" column={column} align="right" />,
            cell: ({ row }) => (
                <div className="text-right">
                    <button
                        type="button"
                        className="text-primary underline underline-offset-2 hover:text-primary/80"
                        onClick={(event) => {
                            event.stopPropagation();
                            setReportReadyCampaign(row.original);
                        }}
                    >
                        {row.original.reportReadyImagesCount}
                    </button>
                </div>
            )
        },
        {
            accessorKey: "storeDoneCount",
            meta: { title: "Stores Done" },
            header: ({ column }) => <SortableHeader label="Stores Done" column={column} align="right" />,
            cell: ({ row }) => (
                <div className="text-right">{row.original.storeDoneCount}</div>
            )
        },
        {
            accessorKey: "storePartialCount",
            meta: { title: "Stores Partial" },
            header: ({ column }) => <SortableHeader label="Stores Partial" column={column} align="right" />,
            cell: ({ row }) => (
                <div className="text-right">{row.original.storePartialCount}</div>
            )
        },
        {
            accessorKey: "storePendingCount",
            meta: { title: "Stores Pending" },
            header: ({ column }) => <SortableHeader label="Stores Pending" column={column} align="right" />,
            cell: ({ row }) => (
                <div className="text-right">{row.original.storePendingCount}</div>
            )
        },
        {
            accessorKey: "storeIssueCount",
            meta: { title: "Stores with Issues" },
            header: ({ column }) => <SortableHeader label="Stores with Issues" column={column} align="right" />,
            cell: ({ row }) => (
                <div className="text-right">{row.original.storeIssueCount}</div>
            )
        },
        {
            accessorKey: "totalStores",
            meta: { title: "Total Stores" },
            header: ({ column }) => <SortableHeader label="Total Stores" column={column} align="right" />,
            cell: ({ row }) => (
                <div className="text-right">{row.original.totalStores}</div>
            )
        }
    ], [handleCampaignChange]);

    // Fetch campaign tracker data
    const { data, isLoading, isRefetching, refetch, dataUpdatedAt } = useQuery<TrackerCampaignResponse | null>({
        queryKey: ["tracker-campaign", cycleId, selectedCampaignId, taskDefinitionIds],
        queryFn: async () => {
            if (!cycleId || !selectedCampaignId) return null;
            return await getJson<TrackerCampaignResponse>(
                `/api/analytics/tracker/campaign/${cycleId}/${selectedCampaignId}${trackerApiQueryString(taskDefinitionIds)}`,
            );
        },
        enabled: !!cycleId && !!selectedCampaignId,
        refetchInterval: selectedCampaignId ? pollInterval : false,
        staleTime: 10000
    });

    const stats = data?.stats;
    const markers = data?.markers || [];
    const selectedCampaignName = selectedCampaignSummary?.campaignName || stats?.campaignName || "";
    const storeCompletionPercent = markers.length > 0 && stats
        ? Math.round((stats.doneCount / markers.length) * 100)
        : 0;

    const handleMarkerClick = (marker: TrackerCampaignMarker) => {
        setSelectedMarker(marker);
    };

    useEffect(() => {
        if (!shouldScrollToCampaignRef.current) return;
        if (!selectedCampaignId) return;
        if (!selectedCampaignSummary && !data) return;

        requestAnimationFrame(() => {
            campaignFocusRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        });
        shouldScrollToCampaignRef.current = false;
    }, [selectedCampaignId, selectedCampaignSummary, data]);

    const getStatusBadge = (status: TrackerRouteStatus) => {
        switch (status) {
            case "DONE": return <Badge variant="secondary" className="bg-green-100 text-green-700">Done</Badge>;
            case "PARTIAL": return <Badge variant="warning" className="text-xs">Partial</Badge>;
            case "ACTIVE": return <Badge className="bg-blue-500 hover:bg-blue-600">Active</Badge>;
            case "PENDING": return <Badge variant="outline" className="text-gray-400">Pending</Badge>;
            case "ISSUE": return <Badge variant="destructive">Issue</Badge>;
            case "ORPHAN": return <Badge variant="outline" className="text-purple-500 border-purple-200 bg-purple-50">Orphan</Badge>;
            default: return <Badge variant="outline">{status}</Badge>;
        }
    };

    return (
            <TrackerLayout
            title="Campaign Tracker"
            lastUpdated={dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : null}
            onRefresh={() => refetch()}
            isRefreshing={isRefetching}
        >
            {!cycleId ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                    Select a cycle to view campaign tracker
                </div>
            ) : (
                <div className="space-y-6 max-w-7xl mx-auto">
                    <Card>
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between gap-3">
                                <CardTitle className="text-base">Campaign Progress</CardTitle>
                                {campaignExportCsv && exportContext && (
                                    <TrackerProgressExportButton
                                        csv={campaignExportCsv}
                                        filenameStem={buildTrackerExportFilenameStem('campaigns', exportContext.cycleName)}
                                    />
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            {progressLoading && (
                                <div className="text-sm text-muted-foreground">Loading campaign progress...</div>
                            )}
                            {!progressLoading && (!campaignProgressData || campaignProgressData.campaigns.length === 0) && (
                                <div className="text-sm text-muted-foreground">No campaigns found for this cycle.</div>
                            )}
                            {campaignProgressData && campaignProgressData.campaigns.length > 0 && (
                                <div className="space-y-3">
                                    <p className="text-xs text-muted-foreground">
                                        This table shows campaign progress by store (same status model as cards/map below).
                                    </p>
                                    <DataTable
                                        columns={campaignProgressColumns}
                                        data={campaignProgressData.campaigns}
                                        searchKey="campaignName"
                                        searchPlaceholder="Search campaigns..."
                                        showColumnToggle
                                        columnVisibilityStorageKey={CAMPAIGN_TABLE_COLUMNS_STORAGE_KEY}
                                        initialColumnVisibility={{
                                            retailerReadyImagesCount: false,
                                            reportReadyImagesCount: false,
                                        }}
                                        disablePagination
                                        onRowClick={(campaign) => handleCampaignChange(String(campaign.campaignId), { scrollToDetails: true })}
                                    />
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <div ref={campaignFocusRef} className="space-y-6">
                        {selectedCampaignId && (selectedCampaignSummary || stats) && (
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base">
                                        Campaign Summary: {selectedCampaignName}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Status</p>
                                            <p className="text-sm font-medium capitalize">
                                                {selectedCampaignSummary?.status || "unknown"}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Store Completion</p>
                                            <p className="text-sm font-medium">{storeCompletionPercent}%</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Stores Done</p>
                                            <p className="text-sm font-medium">{stats?.doneCount ?? "-"}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Stores</p>
                                            <p className="text-sm font-medium">{markers.length}</p>
                                        </div>
                                    </div>
                                    <p className="mt-4 text-xs text-muted-foreground">
                                        This summary is store-level (map status). Table columns above labeled "(Tasks)" are task-level counts.
                                    </p>
                                </CardContent>
                            </Card>
                        )}

                        {/* Campaign Selector */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">Select Campaign</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-wrap gap-4 items-center">
                                    <div className="min-w-[300px]">
                                        <Select
                                            value={selectedCampaignId?.toString() || ""}
                                            onValueChange={handleCampaignChange}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a campaign" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {campaigns.map(campaign => (
                                                    <SelectItem key={campaign.id} value={campaign.id.toString()}>
                                                        {campaign.name}
                                                        {campaign.retailerName && (
                                                            <span className="text-muted-foreground ml-2">
                                                                ({campaign.retailerName})
                                                            </span>
                                                        )}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {!selectedCampaignId && (
                                        <span className="text-sm text-muted-foreground">
                                            Select a campaign to view posting progress
                                        </span>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Stats */}
                        {stats && (
                            <div className="space-y-2">
                            <p className="text-xs text-muted-foreground">Store status for the selected campaign</p>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <Card>
                                <CardContent className="pt-6">
                                    <div className="text-2xl font-bold text-green-600">{stats.doneCount}</div>
                                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mt-1">Stores Done</p>
                                </CardContent>
                            </Card>
                            <Card className={stats.partialCount > 0 ? "border-amber-200" : ""}>
                                <CardContent className="pt-6">
                                    <div className={`text-2xl font-bold ${stats.partialCount > 0 ? "text-amber-500" : "text-gray-400"}`}>
                                        {stats.partialCount || "-"}
                                    </div>
                                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mt-1">Stores Partial</p>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="pt-6">
                                    <div className="text-2xl font-bold text-red-500">{stats.pendingCount}</div>
                                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mt-1">Stores Pending</p>
                                </CardContent>
                            </Card>
                            <Card className={stats.issueCount > 0 ? "border-orange-200" : ""}>
                                <CardContent className="pt-6">
                                    <div className={`text-2xl font-bold ${stats.issueCount > 0 ? "text-orange-500" : "text-gray-400"}`}>
                                        {stats.issueCount || "-"}
                                    </div>
                                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mt-1">Stores with Issues</p>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="pt-6">
                                    <div className="text-2xl font-bold">{markers.length}</div>
                                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mt-1">Stores</p>
                                </CardContent>
                            </Card>
                            </div>
                            </div>
                        )}
                    </div>

                    {/* Map + Store Detail Split */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {/* Map */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">
                                    Store Locations
                                    {markers.length > 0 && (
                                        <span className="text-sm font-normal text-muted-foreground ml-2">
                                            ({markers.length})
                                        </span>
                                    )}
                                </CardTitle>
                                <div className="pt-2">
                                    <TrackerMapLegend />
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="h-[400px] w-full overflow-hidden rounded-lg border">
                                    <MapContainer
                                        center={DEFAULT_MAP_CENTER}
                                        zoom={7}
                                        className="h-full w-full"
                                        scrollWheelZoom
                                    >
                                        <TileLayer
                                            attribution="&copy; OpenStreetMap contributors"
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        />
                                        {markers.length > 0 && <FitBounds markers={markers} />}
                                        {markers.map(marker => (
                                            <CircleMarker
                                                key={marker.routeItemId}
                                                center={[marker.lat, marker.lng]}
                                                radius={10}
                                                fillColor={getStatusColor(marker.status)}
                                                fillOpacity={0.9}
                                                color={selectedMarker?.routeItemId === marker.routeItemId ? "#000" : "#fff"}
                                                weight={selectedMarker?.routeItemId === marker.routeItemId ? 3 : 2}
                                                eventHandlers={{ click: () => handleMarkerClick(marker) }}
                                            >
                                                <Popup>
                                                    <div className="space-y-2 min-w-[180px]">
                                                        <div className="font-semibold">{marker.storeName}</div>
                                                        <div className="flex items-center gap-2">
                                                            {getStatusBadge(marker.status)}
                                                            <span className="text-xs">{marker.postings.length} format(s)</span>
                                                        </div>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="w-full mt-2"
                                                            onClick={() => handleMarkerClick(marker)}
                                                        >
                                                            View Details
                                                        </Button>
                                                    </div>
                                                </Popup>
                                            </CircleMarker>
                                        ))}
                                    </MapContainer>
                                </div>
                                {!selectedCampaignId && (
                                    <div className="mt-3 text-sm text-muted-foreground text-center">
                                        Select a campaign above to view store locations.
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Store Detail Panel */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">
                                    {selectedMarker ? selectedMarker.storeName : "Select a store"}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="max-h-[400px] overflow-auto">
                                {!selectedMarker ? (
                                    <div className="text-center text-muted-foreground py-8">
                                        Click a marker on the map to see posting details
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2">
                                            {getStatusBadge(selectedMarker.status)}
                                        </div>

                                        <Separator />

                                        {/* Postings */}
                                        {selectedMarker.postings.length > 0 && (
                                            <div className="space-y-3">
                                                <div className="text-sm font-medium flex items-center gap-2">
                                                    <Package className="h-4 w-4 text-blue-500" />
                                                    Postings ({selectedMarker.postings.length})
                                                </div>
                                                {selectedMarker.postings.map((task, idx) => (
                                                    <TaskCard key={idx} task={task} onPhotoClick={setLightboxPhoto} />
                                                ))}
                                            </div>
                                        )}

                                        {/* Removals */}
                                        {selectedMarker.removals && selectedMarker.removals.length > 0 && (
                                            <div className="space-y-3">
                                                <div className="text-sm font-medium flex items-center gap-2">
                                                    <Trash2 className="h-4 w-4 text-orange-500" />
                                                    Removals ({selectedMarker.removals.length})
                                                </div>
                                                {selectedMarker.removals.map((task, idx) => (
                                                    <TaskCard key={idx} task={task} onPhotoClick={setLightboxPhoto} />
                                                ))}
                                            </div>
                                        )}

                                        {selectedMarker.postings.length === 0 && (!selectedMarker.removals || selectedMarker.removals.length === 0) && (
                                            <div className="text-sm text-muted-foreground italic">No tasks</div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Store List */}
                    {markers.length > 0 && (
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">All Stores ({markers.length})</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2 max-h-[300px] overflow-auto">
                                    {markers.map(marker => (
                                        <button
                                            key={marker.routeItemId}
                                            type="button"
                                            onClick={() => handleMarkerClick(marker)}
                                            className={`w-full text-left rounded-lg border px-4 py-3 hover:bg-muted transition-colors ${
                                                selectedMarker?.routeItemId === marker.routeItemId ? "ring-2 ring-primary bg-muted" : ""
                                            }`}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-3">
                                                    <span
                                                        className="w-3 h-3 rounded-full flex-shrink-0"
                                                        style={{ backgroundColor: getStatusColor(marker.status) }}
                                                    />
                                                    <div>
                                                        <div className="font-medium">{marker.storeName}</div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {marker.postings.length} format{marker.postings.length !== 1 ? "s" : ""} • 
                                                            {marker.postings.reduce((s, p) => s + p.photoCount, 0)} photos
                                                        </div>
                                                    </div>
                                                </div>
                                                {getStatusBadge(marker.status)}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Lightbox - z-[1000] to appear above Leaflet maps (which use z-index 400-500) */}
            {lightboxPhoto && (
                <div
                    className="fixed inset-0 z-[1000] bg-black/90 flex items-center justify-center p-4"
                    onClick={() => setLightboxPhoto(null)}
                >
                    <button
                        type="button"
                        className="absolute top-4 right-4 text-white hover:text-gray-300 p-2"
                        onClick={() => setLightboxPhoto(null)}
                        title="Close lightbox"
                        aria-label="Close lightbox"
                    >
                        <X className="h-8 w-8" />
                    </button>
                    <img
                        src={lightboxPhoto}
                        alt="Evidence full size"
                        className="max-w-full max-h-full object-contain rounded-lg"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}

            <Dialog open={!!favouritesCampaign} onOpenChange={(open) => !open && setFavouritesCampaign(null)}>
                <DialogContent className="max-w-6xl">
                    <DialogHeader>
                        <DialogTitle>
                            Retailer Ready Images
                            {favouritesCampaign ? ` — ${favouritesCampaign.campaignName}` : ""}
                        </DialogTitle>
                    </DialogHeader>
                    {favouritesCampaign && (
                        <ApprovedInstallerImagesTab
                            campaignId={favouritesCampaign.campaignId}
                            cycleId={cycleId ? Number(cycleId) : undefined}
                        />
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={!!reportReadyCampaign} onOpenChange={(open) => !open && setReportReadyCampaign(null)}>
                <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            Report Ready Images
                            {reportReadyCampaign ? ` — ${reportReadyCampaign.campaignName}` : ""}
                        </DialogTitle>
                    </DialogHeader>
                    {reportReadyCampaign && (
                        <ReportReadyImagesTab campaignId={reportReadyCampaign.campaignId} />
                    )}
                </DialogContent>
            </Dialog>
        </TrackerLayout>
    );
}
